import React, { useState, useEffect } from 'react'
import { useAuthState } from '../../auth/authTools'
import { AuthError } from '../../auth/authTools'
import { useUnifiedAuth } from '../../auth/AuthProvider'
import { getAuth0EnvConfig, getAuthentikEnvConfig, consumeAutoLoginIntent } from '../../auth/authConfig'
import { ErrorMessage } from '../atoms/ErrorMessage'

interface LoginFormProps {
  className?: string
}

// True when more than one auth provider is configured/enabled, so offering to switch
// providers is meaningful. Single-provider deployments hide the option entirely.
const hasMultipleProviders = (): boolean => {
  let count = 0
  if (getAuth0EnvConfig().enabled) count++
  if (getAuthentikEnvConfig()?.enabled) count++
  return count > 1
}

export const LoginForm: React.FC<LoginFormProps> = ({ className = '' }) => {
  const { isAuthenticated, isLoading, error: authError, login, clearError } = useAuthState()
  const { switchProvider, provider } = useUnifiedAuth()
  const [loginError, setLoginError] = useState<AuthError | null>(null)
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const showProviderSwitch = hasMultipleProviders()
  const providerLabel = provider === 'authentik' ? 'Authentik' : provider === 'auth0' ? 'Auth0' : null

  // Handle Auth0 errors
  useEffect(() => {
    if (authError) {
      setLoginError(authError)
      setIsLoggingIn(false)
    }
  }, [authError])

  const handleLogin = async () => {
    try {
      setLoginError(null)
      setIsLoggingIn(true)
      
      await login({
        authorizationParams: {
          redirect_uri: `${window.location.origin}/callback`,
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: 'openid profile email read:ping_feedback read:ping_apps read:ping_users write:ping_feedback write:ping_apps admin:ping_system'
        }
      })
    } catch (error: any) {
      console.error('Login failed:', error)
      setLoginError({
        type: 'auth0',
        code: error?.code || 'login_failed',
        message: error?.message || 'Login failed',
        userMessage: 'Unable to sign in. Please try again.'
      })
      setIsLoggingIn(false)
    }
  }

  const handleRetry = () => {
    setLoginError(null)
    clearError() // Clear URL-based Auth0 errors
    handleLogin()
  }

  // Auto-start login when the user just chose this provider in the selector, so they don't
  // have to click "Sign in" a second time. Runs once (intent is consumed); a cancelled or
  // failed login falls back to the manual button below.
  useEffect(() => {
    if (isLoading || isAuthenticated || isLoggingIn) return
    if (consumeAutoLoginIntent()) {
      handleLogin()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated])

  const getButtonText = () => {
    if (isLoggingIn) return 'Signing in...'
    if (loginError?.code === 'invalid_credentials') return 'Try Different Credentials'
    if (loginError?.code === 'user_not_found') return 'Check Email & Try Again'
    if (loginError?.code === 'account_blocked') return 'Account Blocked - Contact Support'
    if (loginError?.type === 'network') return 'Retry Connection'
    if (loginError?.type === 'authorization') return 'Contact Support'
    if (loginError) return 'Try Again'
    return providerLabel ? `Sign in with ${providerLabel}` : 'Sign in to Ping'
  }

  const isButtonDisabled = () => {
    return isLoading || 
           isAuthenticated || 
           isLoggingIn ||
           loginError?.type === 'authorization' ||
           loginError?.code === 'account_blocked'
  }

  const handleButtonClick = () => {
    if (loginError?.type === 'authorization' || loginError?.code === 'account_blocked') {
      window.open('mailto:support@crossmoore.io?subject=Ping Pong Feedback Access Request - Account Issue', '_blank')
      return
    }
    
    if (loginError) {
      handleRetry()
    } else {
      handleLogin()
    }
  }

  const getErrorTitle = (error: AuthError) => {
    // Handle specific error codes first
    switch (error.code) {
      case 'invalid_credentials': return 'Invalid Credentials'
      case 'user_not_found': return 'User Not Found'
      case 'account_blocked': return 'Account Blocked'
      case 'admin_unauthorized': return 'Access Restricted'
      case 'login_required': return 'Login Required'
      case 'consent_required': return 'Consent Required'
      case 'interaction_required': return 'Interaction Required'
    }
    
    // Fall back to error types
    switch (error.type) {
      case 'authorization': return 'Access Restricted'
      case 'network': return 'Connection Issue'
      case 'auth0': return 'Authentication Error'
      case 'token_validation': return 'Session Expired'
      default: return 'Unexpected Error'
    }
  }

  return (
    <div className={`flex flex-col items-center space-y-4 ${className}`}>
      <button
        onClick={handleButtonClick}
        data-testid="login-button"
        disabled={isButtonDisabled()}
        className={`
          w-full max-w-md px-8 py-4 
          bg-primary-600/90 hover:bg-primary-700/90 
          border border-primary-500/50 hover:border-primary-400/70
          backdrop-blur-sm 
          rounded-lg 
          text-white font-medium text-lg
          transition-all duration-200
          disabled:opacity-60 disabled:cursor-not-allowed
          ${loginError ? 'border-red-400/50 bg-red-500/10' : ''}
        `}
      >
        <div className="flex items-center justify-center space-x-3">
          {/* Ping Logo */}
          <img 
            src="/logo-ping-blue-white-large.png" 
            alt="Ping" 
            className="w-8 h-8 opacity-90"
          />
          
          {/* Button Text */}
          <div className="flex flex-col items-start space-y-1">
            <span className="text-sm opacity-80">
              {getButtonText()}
            </span>
            <span className="text-xs opacity-60">
              Ping Pong Feedback
            </span>
          </div>
        </div>
      </button>

      {showProviderSwitch && (
        <button
          type="button"
          onClick={switchProvider}
          data-testid="switch-provider-button"
          disabled={isLoggingIn}
          className="text-sm text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Use a different sign-in method
        </button>
      )}

      {loginError && (
        <ErrorMessage
          title={getErrorTitle(loginError)}
          message={loginError.userMessage}
          variant="error"
          className="w-full max-w-md"
        />
      )}

      {/* Development debug info */}
      {import.meta.env.DEV && loginError && (
        <div className="w-full max-w-md p-2 bg-blue-500/10 border border-blue-400/30 rounded text-blue-300 text-xs">
          <div className="font-medium">Debug Info</div>
          <div>Type: {loginError.type} | Code: {loginError.code || 'N/A'}</div>
        </div>
      )}
    </div>
  )
} 